import { useMemo } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
import GroupsIcon from '@mui/icons-material/Groups';
// import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SettingsIcon from '@mui/icons-material/Settings';
// import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { usePartnerPlansQuery } from '../../api/use-partner-plans-query';
import PortalShell from '../layout/PortalShell';
import PartnerTemporaryDisableOverlay from './PartnerTemporaryDisableOverlay';
import {
  canShowPartnerSubscriptionNav,
  isPartnerSubscriptionPath,
  isPartnerSubscriptionRequired,
  PARTNER_SUBSCRIPTION_PATH,
} from '../../utils/partnerSubscriptionAccess';

const PARTNER_NAV = [
  { label: 'Dashboard', path: '/partner-dashboard', icon: DashboardIcon },
  { label: 'Events', path: '/partner-events', icon: EventIcon },
  { label: 'Community', path: '/partner-community', icon: GroupsIcon },
  // { label: 'Aria Handsoff', path: '/partner-aria-handsoff', icon: SupportAgentIcon },
  // { label: 'Analytics', path: '/partner-analytics', icon: BarChartIcon },
  // {
  //   label: 'Manage Subscription',
  //   path: PARTNER_SUBSCRIPTION_PATH,
  //   icon: CardMembershipOutlinedIcon,
  // },
  { label: 'Settings', path: '/partner-settings', icon: SettingsIcon },
];

const PartnerLayout = ({ children, onSignOut }) => {
  const { partnerProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const showSubscriptionNav = canShowPartnerSubscriptionNav(partnerProfile);

  const { data: plansData, isSuccess: plansLoaded } = usePartnerPlansQuery({
    enabled: showSubscriptionNav,
  });

  const subscriptionRequired =
    showSubscriptionNav &&
    plansLoaded &&
    isPartnerSubscriptionRequired(
      partnerProfile,
      plansData?.currentPlan ?? partnerProfile?.currentPlan,
    );

  const isTemporarilyDisabled = Boolean(partnerProfile?.temporaryDisable);

  const navItems = useMemo(
    () =>
      PARTNER_NAV.filter(
        (item) => item.path !== PARTNER_SUBSCRIPTION_PATH || showSubscriptionNav,
      ).map((item) => ({
        ...item,
        disabled: Boolean(subscriptionRequired && item.path !== PARTNER_SUBSCRIPTION_PATH),
      })),
    [showSubscriptionNav, subscriptionRequired],
  );

  const handleSignOut = async () => {
    await onSignOut?.();
    navigate('/partner-login', { replace: true });
  };

  const user = {
    name: partnerProfile?.fullName || 'Partner',
    email: partnerProfile?.email || '',
    avatarUrl: partnerProfile?.avatarUrl || undefined,
  };

  const shouldForceSubscription =
    subscriptionRequired && !isPartnerSubscriptionPath(location.pathname);

  return (
    <PortalShell
      navItems={navItems}
      user={user}
      notificationsPath="/partner-notifications"
      onSignOut={handleSignOut}
      signOutRedirectPath="/partner-login"
      overlay={
        isTemporarilyDisabled ? <PartnerTemporaryDisableOverlay onSignOut={handleSignOut} /> : null
      }
    >
      {shouldForceSubscription ? <Navigate to={PARTNER_SUBSCRIPTION_PATH} replace /> : children}
    </PortalShell>
  );
};

export default PartnerLayout;
